// journal-voucher-list.component.ts
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import moment from 'moment';
import * as XLSX from 'xlsx';

import { JournalVoucherService } from '../services/journal-voucher/journal-voucher.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { DynamicListingComponent, FilterConfig, ColumnConfig, ActionConfig, ActionEvent, FilterEvent } from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';

@Component({
  selector: 'lib-journal-voucher-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent],
  templateUrl: './journal-voucher-list.html',
  styleUrls: ['./journal-voucher-list.css'],
})
export class JournalVoucherList implements OnInit {
  isLoading = false;
  exportFilename = `journal_vouchers_${new Date().toISOString().slice(0, 10)}`;

  tableData: any[] = [];

  postingDate: Date | null = null;
  transactionNo = '';
  status = '';

  filterConfigs: FilterConfig[] = [];

  columnConfigs: ColumnConfig[] = [
    { key: 'transaction_no', header: 'Transaction No.', type: 'text' },
    { key: 'posting_date', header: 'Posting Date', type: 'date' },
    { key: 'posting_period', header: 'Posting Period', type: 'text' },
    { key: 'subsidiary', header: 'Subsidiary', type: 'text', ellipsis: true },
    { key: 'memo', header: 'Memo', type: 'text', ellipsis: true },
    { key: 'status', header: 'Status', type: 'text' },
    { key: 'total_debit', header: 'Total Debit', type: 'number' },
    { key: 'total_credit', header: 'Total Credit', type: 'number' },
    { key: 'created_at', header: 'Created Date', type: 'date' },
  ];

  actionConfigs: ActionConfig[] = [
    { key: 'view', label: 'View', tooltip: 'View journal voucher', severity: 'secondary', svgIcon: SVG_ICONS.VIEW },
    { key: 'edit', label: 'Edit', tooltip: 'Edit journal voucher', severity: 'secondary', svgIcon: SVG_ICONS.EDIT },
    { key: 'delete', label: 'Delete', tooltip: 'Delete journal voucher', severity: 'secondary', svgIcon: SVG_ICONS.DELETE },
  ];

  pageIndex = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 0;

  constructor(
    private router: Router,
    private journalVoucherService: JournalVoucherService,
    private toasterService: ToasterService
  ) {}

  async ngOnInit() {
    this.initializeFilterConfigs();
    await this.loadJournalVouchers();
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'postingDate',
        type: 'date',
        placeholder: 'Posting Date',
        value: this.postingDate,
        showInAccordion: true,
      },
      {
        key: 'transactionNo',
        type: 'text',
        placeholder: 'Transaction No.',
        value: this.transactionNo,
        showInAccordion: true,
      },
      {
        key: 'status',
        type: 'select',
        placeholder: 'Status',
        value: this.status,
        options: [
          { label: 'Draft', value: 'draft' },
          { label: 'Posted', value: 'posted' },
          { label: 'Voided', value: 'voided' },
        ],
        optionLabel: 'label',
        optionValue: 'value',
        showInAccordion: true,
      },
    ];
  }

  async loadJournalVouchers(page: number = 1) {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const filters: any = {
        limit: this.rowsPerPage,
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        is_deleted: false,
      };

      if (this.transactionNo?.trim()) {
        filters.transaction_no = this.transactionNo.trim();
      }

      if (this.postingDate) {
        filters.posting_date = moment(this.postingDate).format('YYYY-MM-DD');
      }

      if (this.status) {
        filters.status = this.status;
      }

      const response = await lastValueFrom(this.journalVoucherService.getJournalVouchers(filters));
      const journalVouchers = response?.data?.journal_vouchers ?? [];

      this.tableData = journalVouchers.map((jv: any) => {
        // Calculate totals from lines
        const lines = jv.journal_voucher_lines || [];
        const totalDebit = lines.reduce((sum: number, line: any) => sum + (parseFloat(String(line.debit || 0))), 0);
        const totalCredit = lines.reduce((sum: number, line: any) => sum + (parseFloat(String(line.credit || 0))), 0);

        return {
          id: jv.id ?? '',
          transaction_no: jv.transaction_no ?? '',
          posting_date: jv.posting_date ?? '',
          posting_period: jv.posting_period?.period_name ?? jv.posting_period ?? '',
          subsidiary: jv.subsidiary?.name ?? jv.subsidiary_id ?? '',
          memo: jv.memo ?? '',
          status: jv.status ?? '',
          total_debit: totalDebit.toFixed(2),
          total_credit: totalCredit.toFixed(2),
          created_at: jv.created_at ?? '',
        };
      });

      this.totalCount = response?.data?.total ?? this.tableData.length;
      this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.rowsPerPage));
    } catch (error) {
      console.error('Error loading journal vouchers:', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.toasterService.error('Error', 'Failed to load journal vouchers');
    } finally {
      this.isLoading = false;
    }
  }

  onFilterEvent(event: FilterEvent) {
    this.postingDate = event.data?.postingDate ?? null;
    this.transactionNo = event.data?.transactionNo ?? '';
    this.status = event.data?.status ?? '';

    this.pageIndex = 1;
    this.loadJournalVouchers(this.pageIndex);
  }

  onColumnsSelected(_: string[]) {
    // Future: save column preferences
  }

  onActionClicked(event: ActionEvent) {
    const journalVoucherId = event.row?.id;
    if (!journalVoucherId) return;

    switch (event.action) {
      case 'view':
        this.view(journalVoucherId);
        break;
      case 'edit':
        this.edit(journalVoucherId);
        break;
      case 'delete':
        this.delete(journalVoucherId);
        break;
    }
  }

  onPageChanged(page: number) {
    this.loadJournalVouchers(page);
  }

  // CSV export
  exportCsv() {
    if (!this.tableData.length) {
      this.toasterService.info('Info', 'No data available to export.');
      return;
    }

    const headers = [
      'S.No.', 'Transaction No.', 'Posting Date', 'Posting Period',
      'Subsidiary', 'Memo', 'Status', 'Total Debit', 'Total Credit', 'Created Date'
    ] as const;

    type ExportRow = { [K in typeof headers[number]]: string | number };

    const rows: ExportRow[] = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'Transaction No.': row.transaction_no ?? '',
      'Posting Date': row.posting_date ?? '',
      'Posting Period': row.posting_period ?? '',
      'Subsidiary': row.subsidiary ?? '',
      'Memo': row.memo ?? '',
      'Status': row.status ?? '',
      'Total Debit': row.total_debit ?? '0.00',
      'Total Credit': row.total_credit ?? '0.00',
      'Created Date': row.created_at ?? '',
    }));

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        headers
          .map(header => {
            const cell = row[header] ?? '';
            const escaped = cell.toString().replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(',')
      ),
    ].join('\n');

    // BOM for Excel UTF-8 support
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${this.exportFilename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // XLSX export
  exportXlsx() {
    if (!this.tableData.length) {
      this.toasterService.info('Info', 'No data available to export.');
      return;
    }

    const headers = [
      'S.No.', 'Transaction No.', 'Posting Date', 'Posting Period',
      'Subsidiary', 'Memo', 'Status', 'Total Debit', 'Total Credit', 'Created Date'
    ] as const;

    const data = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'Transaction No.': row.transaction_no ?? '',
      'Posting Date': row.posting_date ?? '',
      'Posting Period': row.posting_period ?? '',
      'Subsidiary': row.subsidiary ?? '',
      'Memo': row.memo ?? '',
      'Status': row.status ?? '',
      'Total Debit': row.total_debit ?? '0.00',
      'Total Credit': row.total_credit ?? '0.00',
      'Created Date': row.created_at ?? '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = headers.map(() => ({ wch: 20 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Journal Vouchers');
    XLSX.writeFile(workbook, `${this.exportFilename}.xlsx`);
  }

  // PDF export
  exportPdf() {
    window.print();
  }

  view(journalVoucherId: string | number) {
    this.router.navigate(['/finance/journal-vouchers', journalVoucherId, 'view']);
  }

  edit(journalVoucherId: string | number) {
    this.router.navigate(['/finance/journal-vouchers', journalVoucherId, 'edit']);
  }

  async delete(journalVoucherId: string | number) {
    if (!confirm('Are you sure you want to delete this journal voucher?')) return;

    try {
      this.isLoading = true;
      await lastValueFrom(this.journalVoucherService.deleteJournalVoucher(journalVoucherId));
      this.toasterService.success('Success', 'Journal voucher deleted successfully');
      await this.loadJournalVouchers(this.pageIndex);
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Failed to delete journal voucher';
      this.toasterService.error('Error', message);
    } finally {
      this.isLoading = false;
    }
  }
}

